import React, { useContext, useMemo } from 'react';
import { FilterContext } from '@/pages/dashboard/components/PageContainer/context';
import { getLanguage } from '@home/utils/common';
import { useLocal } from '@/pages/dashboard/utils/customHook';
import NoData from '@/pages/dashboard/components/NoData';
import Loading from '@/pages/dashboard/components/Loadings';
import './index.scss';

interface IEventFilterRequiredWrapProps {
  children?: React.ReactNode;
  tableTitle?: { titleEn: string; titleCn: string; };
  placeholderTipsEn?: string;
  placeholderTipsCn?: string;
}

/**
 * 筛选器加载完成后，若事件筛选不完整则展示 NoData 占位，否则渲染子组件
 * 筛选器加载中时展示 loading 样式
 * 注意：PageContainer Render 会将 config.props 展开为顶层 props 传入
 */
const EventFilterRequiredWrap: React.FC<IEventFilterRequiredWrapProps> = (props) => {
  const {
    children,
    tableTitle = { titleEn: 'Event Exploration', titleCn: '事件探索' },
    placeholderTipsEn = 'Please apply【Event Filter】 to view data.',
    placeholderTipsCn = '筛选器设置【事件筛选】后，即可查看对应数据。',
  } = props;
  const { filterVal, filterLoading } = useContext(FilterContext) || {};
  const { isEN } = useLocal();

  const isEventFilterComplete = useMemo(() => {
    const hasEventName = (filterVal?.event_name?.length ?? 0) > 0;
    const hasHours = (filterVal?.hours_since_register?.length ?? 0) > 0;
    const hasPvThreshold = Array.isArray(filterVal?.pv_threshold) && filterVal.pv_threshold.some((v: unknown) => !!v);
    return hasEventName && hasHours && hasPvThreshold;
  }, [filterVal]);

  const title = getLanguage(isEN, tableTitle?.titleEn ?? '', tableTitle?.titleCn ?? '');
  const placeholderTips = getLanguage(isEN, placeholderTipsEn, placeholderTipsCn);

  // 筛选器加载中：展示 loading
  if (filterLoading) {
    return (
      <div className="dbd-event-filter-required-wrap">
        <div className="dbd-event-filter-required-wrap__placeholder dbd-event-filter-required-wrap__placeholder--loading">
          <h3 className="dbd-event-filter-required-wrap__title">{title}</h3>
          <Loading className="dbd-event-filter-required-wrap__loading" />
        </div>
      </div>
    );
  }

  // 筛选器加载完成但事件筛选不完整：展示 NoData 占位
  if (!isEventFilterComplete) {
    return (
      <div className="dbd-event-filter-required-wrap">
        <div className="dbd-event-filter-required-wrap__placeholder">
          <h3 className="dbd-event-filter-required-wrap__title">{title}</h3>
          <NoData className="dbd-event-filter-required-wrap__tips" text={placeholderTips} />
        </div>
      </div>
    );
  }

  return <>{children}</>;
};

export default EventFilterRequiredWrap;
